import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./CurrencyManagementPage.css";

const STATUS_VALUES = ["Active", "Inactive"];
const ACTIVE_INACTIVE = ["ACTIVE", "INACTIVE"];
const IMAGE_ACCEPT = "image/jpeg,image/jpg,image/png";

const FALLBACK_REGEX = {
  BEP20: "^(0x)?[0-9a-fA-F]{40}$",
  ERC20: "^(0x)?[0-9a-fA-F]{40}$",
  POLYGON: "^(0x)?[0-9a-fA-F]{40}$",
  TRC20: "^(T|t)[A-Za-z1-9]{33}$",
  SOLANA: "^[1-9A-HJ-NP-Za-km-z]{32,44}$",
};

const validateContractAddress = (value, chain, supportedChains) => {
  if (!value?.trim()) return null;
  const ch = supportedChains?.find((c) => c.id === chain);
  const regexStr = ch?.contractAddressRegex || FALLBACK_REGEX[chain];
  if (!regexStr) return null;
  try {
    const regex = new RegExp(regexStr);
    return regex.test(value.trim()) ? null : `Invalid contract address format for ${chain}`;
  } catch {
    return null;
  }
};

const toObj = (v) => (typeof v === "object" && v !== null ? v : {});

const norm = (v) => (v == null || v === "" ? "(empty)" : String(v));

const getCoboChainKey = (entry) => entry?.tokenId || entry?._id || "";

const parseCoboIsActive = (value) => {
  if (value === true || value === 1 || value === "true" || value === "1") return true;
  if (value === false || value === 0 || value === "false" || value === "0") return false;
  return true;
};

const buildCoboChainDataFromCoin = (coin) => {
  const list = Array.isArray(coin?.cobo_chain_list) ? coin.cobo_chain_list : [];
  const data = {};
  list.forEach((entry) => {
    const key = getCoboChainKey(entry);
    if (!key) return;
    data[key] = {
      _id: entry._id ?? "",
      tokenId: entry.tokenId ?? "",
      chainId: entry.chainId ?? "",
      chainName: entry.chainName ?? entry.chainId ?? "",
      decimals: entry.decimals ?? "",
      confirmations: entry.confirmations ?? "",
      requireMemo: Boolean(entry.requireMemo),
      isActive: parseCoboIsActive(entry.isActive),
      minWithdrawalLimit: entry.minWithdrawalLimit ?? "",
      maxWithdrawalLimit: entry.maxWithdrawalLimit ?? "",
      withdrawalFee: entry.withdrawalFee ?? "",
    };
  });
  return data;
};

const appendCoboChainFields = (formData, entry) => {
  if (!entry?.chainId) return;
  formData.append("chainId", entry.chainId);
  formData.append("isActive", entry.isActive ? "true" : "false");
  if (entry.minWithdrawalLimit !== "" && entry.minWithdrawalLimit != null) {
    formData.append("minWithdrawalLimit", String(entry.minWithdrawalLimit));
  }
  if (entry.maxWithdrawalLimit !== "" && entry.maxWithdrawalLimit != null) {
    formData.append("maxWithdrawalLimit", String(entry.maxWithdrawalLimit));
  }
  if (entry.withdrawalFee !== "" && entry.withdrawalFee != null) {
    formData.append("withdrawalFee", String(entry.withdrawalFee));
  }
};

const getCoboChainUpdates = (coin, coboChainData, selectedEntry) => {
  const origCobo = buildCoboChainDataFromCoin(coin);
  const byChainId = new Map();

  if (selectedEntry?.chainId) {
    byChainId.set(selectedEntry.chainId, selectedEntry);
  }

  Object.keys(coboChainData || {}).forEach((key) => {
    const next = coboChainData[key];
    const prev = origCobo[key];
    if (!next?.chainId) return;
    const changed = ["minWithdrawalLimit", "maxWithdrawalLimit", "withdrawalFee", "isActive"].some(
      (field) =>
        field === "isActive"
          ? Boolean(prev?.[field]) !== Boolean(next?.[field])
          : norm(prev?.[field]) !== norm(next?.[field])
    );
    if (changed) {
      byChainId.set(next.chainId, next);
    }
  });

  return [...byChainId.values()];
};

const getChangesSummary = (coin, form) => {
  if (!coin) return [];
  const changes = [];
  const { name, shortName, category, status, description, totalSupply, circulatingSupply, swappingStatus, p2pStatus, minSwapping, maxSwapping, swappingFee, p2pFee, chainData, links } = form;

  if (norm(coin.name) !== norm(name)) changes.push({ field: "Coin Name", from: coin.name || "(empty)", to: name || "(empty)" });
  if (norm(coin.short_name) !== norm(shortName)) changes.push({ field: "Symbol", from: coin.short_name || "(empty)", to: shortName || "(empty)" });
  if (norm(coin.category) !== norm(category)) changes.push({ field: "Category", from: coin.category || "(empty)", to: category || "(empty)" });
  if (norm(coin.status) !== norm(status)) changes.push({ field: "Status", from: coin.status || "(empty)", to: status || "(empty)" });
  if (norm(coin.description) !== norm(description)) changes.push({ field: "Description", from: norm(coin.description), to: norm(description) });
  if (norm(coin.total_supply) !== norm(totalSupply)) changes.push({ field: "Total Supply", from: norm(coin.total_supply), to: norm(totalSupply) });
  if (norm(coin.circulating_supply) !== norm(circulatingSupply)) changes.push({ field: "Circulating Supply", from: norm(coin.circulating_supply), to: norm(circulatingSupply) });
  if (norm(coin.swapping_status) !== norm(swappingStatus)) changes.push({ field: "Swapping Status", from: coin.swapping_status || "(empty)", to: swappingStatus || "(empty)" });
  if (norm(coin.p2p_status) !== norm(p2pStatus)) changes.push({ field: "P2P Status", from: coin.p2p_status || "(empty)", to: p2pStatus || "(empty)" });
  if (norm(coin.min_swapping) !== norm(minSwapping)) changes.push({ field: "Min Swapping", from: norm(coin.min_swapping), to: norm(minSwapping) });
  if (norm(coin.max_swapping) !== norm(maxSwapping)) changes.push({ field: "Max Swapping", from: norm(coin.max_swapping), to: norm(maxSwapping) });
  if (norm(coin.swapping_fee) !== norm(swappingFee)) changes.push({ field: "Swapping Fee", from: norm(coin.swapping_fee), to: norm(swappingFee) });
  if (norm(coin.p2p_fee) !== norm(p2pFee)) changes.push({ field: "P2P Fee", from: norm(coin.p2p_fee), to: norm(p2pFee) });

  const origCh = Array.isArray(coin.chain) ? coin.chain : [];
  const newCh = Object.keys(chainData).sort();
  const allChains = [...new Set([...origCh, ...newCh])];

  origCh.filter((c) => !newCh.includes(c)).forEach((c) => changes.push({ field: "Chain removed", from: c, to: "(removed)" }));
  newCh.filter((c) => !origCh.includes(c)).forEach((c) => changes.push({ field: "Chain added", from: "(none)", to: c }));

  const ca = toObj(coin.contract_address);
  const dec = toObj(coin.decimals);
  const minW = toObj(coin.min_withdrawal);
  const maxW = toObj(coin.max_withdrawal);
  const minD = toObj(coin.min_deposit);
  const maxD = toObj(coin.max_deposit);
  const wFee = toObj(coin.withdrawal_fee);
  const depSt = toObj(coin.deposit_status);
  const withSt = toObj(coin.withdrawal_status);

  allChains.forEach((ch) => {
    const cd = chainData[ch] || {};
    const oAddr = (ca[ch] ?? "").toString().trim();
    const nAddr = (cd.contract_address ?? "").toString().trim();
    if (oAddr !== nAddr) changes.push({ field: `${ch} – Contract Address`, from: oAddr || "(empty)", to: nAddr || "(empty)" });

    const oDec = (dec[ch] ?? "").toString();
    const nDec = (cd.decimals ?? "").toString();
    if (oDec !== nDec) changes.push({ field: `${ch} – Decimals`, from: oDec || "(empty)", to: nDec || "(empty)" });

    const oMinW = (minW[ch] ?? "").toString();
    const nMinW = (cd.min_withdrawal ?? "").toString();
    if (oMinW !== nMinW) changes.push({ field: `${ch} – Min Withdrawal`, from: oMinW || "(empty)", to: nMinW || "(empty)" });

    const oMaxW = (maxW[ch] ?? "").toString();
    const nMaxW = (cd.max_withdrawal ?? "").toString();
    if (oMaxW !== nMaxW) changes.push({ field: `${ch} – Max Withdrawal`, from: oMaxW || "(empty)", to: nMaxW || "(empty)" });

    const oMinD = (minD[ch] ?? "").toString();
    const nMinD = (cd.min_deposit ?? "").toString();
    if (oMinD !== nMinD) changes.push({ field: `${ch} – Min Deposit`, from: oMinD || "(empty)", to: nMinD || "(empty)" });

    const oMaxD = (maxD[ch] ?? "").toString();
    const nMaxD = (cd.max_deposit ?? "").toString();
    if (oMaxD !== nMaxD) changes.push({ field: `${ch} – Max Deposit`, from: oMaxD || "(empty)", to: nMaxD || "(empty)" });

    const oWFee = (wFee[ch] ?? "").toString();
    const nWFee = (cd.withdrawal_fee ?? "").toString();
    if (oWFee !== nWFee) changes.push({ field: `${ch} – Withdrawal Fee`, from: oWFee || "(empty)", to: nWFee || "(empty)" });

    const oDepSt = depSt[ch] === "ACTIVE" || depSt[ch] === "INACTIVE" ? depSt[ch] : "ACTIVE";
    const nDepSt = cd.deposit_status || "ACTIVE";
    if (oDepSt !== nDepSt) changes.push({ field: `${ch} – Deposit Status`, from: oDepSt, to: nDepSt });

    const oWithSt = withSt[ch] === "ACTIVE" || withSt[ch] === "INACTIVE" ? withSt[ch] : "ACTIVE";
    const nWithSt = cd.withdrawal_status || "ACTIVE";
    if (oWithSt !== nWithSt) changes.push({ field: `${ch} – Withdrawal Status`, from: oWithSt, to: nWithSt });
  });

  const origLinks = Array.isArray(coin.links) ? coin.links.map((l) => (typeof l === "object" ? l?.url ?? "" : l)).filter(Boolean) : [];
  const newLinks = links.map((l) => (typeof l === "object" && l ? (l.url || "") : (l || ""))).filter(Boolean);
  if (JSON.stringify(origLinks.sort()) !== JSON.stringify(newLinks.sort())) {
    changes.push({ field: "Links", from: origLinks.length ? origLinks.join(", ") : "(none)", to: newLinks.length ? newLinks.join(", ") : "(none)" });
  }

  if (form.coinImage) changes.push({ field: "Coin Image", from: "(existing)", to: form.coinImage.name || "(new file)" });

  const origCobo = buildCoboChainDataFromCoin(coin);
  const newCobo = form.coboChainData || {};
  const allCoboKeys = [...new Set([...Object.keys(origCobo), ...Object.keys(newCobo)])].sort();

  allCoboKeys.forEach((key) => {
    const label = newCobo[key]?.chainId || origCobo[key]?.chainId || key;
    const tokenLabel = newCobo[key]?.tokenId || origCobo[key]?.tokenId || key;

    const oMin = norm(origCobo[key]?.minWithdrawalLimit);
    const nMin = norm(newCobo[key]?.minWithdrawalLimit);
    if (oMin !== nMin) {
      changes.push({
        field: `${tokenLabel} (${label}) – Min Withdrawal Limit`,
        from: oMin,
        to: nMin,
      });
    }

    const oMax = norm(origCobo[key]?.maxWithdrawalLimit);
    const nMax = norm(newCobo[key]?.maxWithdrawalLimit);
    if (oMax !== nMax) {
      changes.push({
        field: `${tokenLabel} (${label}) – Max Withdrawal Limit`,
        from: oMax,
        to: nMax,
      });
    }

    const oFee = norm(origCobo[key]?.withdrawalFee);
    const nFee = norm(newCobo[key]?.withdrawalFee);
    if (oFee !== nFee) {
      changes.push({
        field: `${tokenLabel} (${label}) – Withdrawal Fee`,
        from: oFee,
        to: nFee,
      });
    }

    const oActive = parseCoboIsActive(origCobo[key]?.isActive);
    const nActive = parseCoboIsActive(newCobo[key]?.isActive);
    if (oActive !== nActive) {
      changes.push({
        field: `${tokenLabel} (${label}) – Chain Active`,
        from: oActive ? "Active" : "Inactive",
        to: nActive ? "Active" : "Inactive",
      });
    }
  });

  return changes;
};

const Section = ({ id, title, icon, openSections, onToggle, children }) => (
  <div className="currency-section">
    <button type="button" className="currency-section__head" onClick={() => onToggle(id)} aria-expanded={openSections.has(id)}>
      <i className={`fas ${icon}`} />
      <span>{title}</span>
      <i className={`fas fa-chevron-${openSections.has(id) ? "up" : "down"} currency-section__chevron`} />
    </button>
    {openSections.has(id) && <div className="currency-section__body">{children}</div>}
  </div>
);

const populateFormFromCoin = (coin, setters) => {
  if (!coin) return;
  const {
    setName, setShortName, setCategory, setStatus, setChainData, setCoboChainData,
    setDescription, setTotalSupply, setCirculatingSupply, setSwappingStatus, setP2pStatus,
    setLinks, setMinSwapping, setMaxSwapping, setSwappingFee, setP2pFee, setSelectedCoboChainKey,
  } = setters;

  setName(coin.name || "");
  setShortName(coin.short_name || "");

  const cat = coin.category || "GLOBAL";
  setCategory(cat === "GLOBAL" || cat === "LOCAL" ? cat : "GLOBAL");

  const st = coin.status || "Active";
  setStatus(st === "Active" || st === "Inactive" ? st : "Active");

  setDescription(coin.description || "");
  setTotalSupply(coin.total_supply ?? "");
  setCirculatingSupply(coin.circulating_supply ?? "");
  setSwappingStatus(coin.swapping_status === "ACTIVE" || coin.swapping_status === "INACTIVE" ? coin.swapping_status : "ACTIVE");
  setP2pStatus(coin.p2p_status === "ACTIVE" || coin.p2p_status === "INACTIVE" ? coin.p2p_status : "ACTIVE");
  setMinSwapping(coin.min_swapping ?? "");
  setMaxSwapping(coin.max_swapping ?? "");
  setSwappingFee(coin.swapping_fee ?? "");
  setP2pFee(coin.p2p_fee ?? "");

  const linkArr = Array.isArray(coin.links) ? coin.links : [];
  setLinks(linkArr.length > 0 ? linkArr.map((l) => (typeof l === "object" ? l.url ?? "" : l)) : [""]);

  const ch = Array.isArray(coin.chain) ? coin.chain : [];
  const ca = toObj(coin.contract_address);
  const dec = toObj(coin.decimals);
  const minW = toObj(coin.min_withdrawal);
  const maxW = toObj(coin.max_withdrawal);
  const minD = toObj(coin.min_deposit);
  const maxD = toObj(coin.max_deposit);
  const wFee = toObj(coin.withdrawal_fee);
  const depSt = toObj(coin.deposit_status);
  const withSt = toObj(coin.withdrawal_status);

  const cd = {};
  ch.forEach((c) => {
    cd[c] = {
      contract_address: ca[c] ?? "",
      decimals: dec[c] ?? "",
      min_withdrawal: minW[c] ?? "",
      max_withdrawal: maxW[c] ?? "",
      min_deposit: minD[c] ?? "",
      max_deposit: maxD[c] ?? "",
      withdrawal_fee: wFee[c] ?? "",
      deposit_status: depSt[c] === "ACTIVE" || depSt[c] === "INACTIVE" ? depSt[c] : "ACTIVE",
      withdrawal_status: withSt[c] === "ACTIVE" || withSt[c] === "INACTIVE" ? withSt[c] : "ACTIVE",
    };
  });
  setChainData(cd);

  const coboData = buildCoboChainDataFromCoin(coin);
  setCoboChainData(coboData);
  const coboKeys = Object.keys(coboData);
  setSelectedCoboChainKey(coboKeys[0] || "");
};

const CurrencyManagementPage = () => {
  const [config, setConfig] = useState(null);
  const [coinsList, setCoinsList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [selectedCoinId, setSelectedCoinId] = useState("");
  const [coinDetail, setCoinDetail] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const errorAlertRef = useRef(null);
  const [openSections, setOpenSections] = useState(new Set(["basic", "chain", "cobo", "withdrawal", "deposit", "status", "info", "links"]));

  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [category, setCategory] = useState("GLOBAL");
  const [status, setStatus] = useState("Active");
  const [chainData, setChainData] = useState({});
  const [coboChainData, setCoboChainData] = useState({});
  const [selectedCoboChainKey, setSelectedCoboChainKey] = useState("");
  const [coinImage, setCoinImage] = useState(null);
  const [description, setDescription] = useState("");
  const [totalSupply, setTotalSupply] = useState("");
  const [circulatingSupply, setCirculatingSupply] = useState("");
  const [swappingStatus, setSwappingStatus] = useState("ACTIVE");
  const [p2pStatus, setP2pStatus] = useState("ACTIVE");
  const [minSwapping, setMinSwapping] = useState("");
  const [maxSwapping, setMaxSwapping] = useState("");
  const [swappingFee, setSwappingFee] = useState("");
  const [p2pFee, setP2pFee] = useState("");
  const [links, setLinks] = useState([]);

  const chains = Object.keys(chainData).sort();
  const coboChainKeys = Object.keys(coboChainData).sort();
  const selectedCoboEntry = selectedCoboChainKey ? coboChainData[selectedCoboChainKey] : null;
  const supportedChains = config?.data?.supportedChains ?? [];

  const fetchConfig = useCallback(async () => {
    const res = await AdminService.getCoinsConfig();
    if (res?.success) setConfig(res);
  }, []);

  const fetchCoinsList = useCallback(async () => {
    setListLoading(true);
    setListError("");
    const res = await AdminService.getCoinsList();
    setListLoading(false);
    if (res?.success && Array.isArray(res.data)) {
      setCoinsList(res.data);
      setSelectedCoinId((prev) => (prev && res.data.some((c) => c._id === prev)) ? prev : (res.data[0]?._id ?? ""));
    } else {
      setListError(res?.message || "Failed to load coins list.");
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchCoinsList();
  }, [fetchCoinsList]);

  useEffect(() => {
    if (selectedCoinId && coinsList.length > 0) {
      const coin = coinsList.find((c) => c._id === selectedCoinId);
      if (coin) {
        setCoinDetail(coin);
        setSubmitError("");
        setFieldErrors({});
        populateFormFromCoin(coin, {
          setName, setShortName, setCategory, setStatus, setChainData, setCoboChainData,
          setDescription, setTotalSupply, setCirculatingSupply, setSwappingStatus, setP2pStatus,
          setLinks, setMinSwapping, setMaxSwapping, setSwappingFee, setP2pFee, setSelectedCoboChainKey,
        });
      } else {
        setCoinDetail(null);
      }
    } else {
      setCoinDetail(null);
    }
  }, [selectedCoinId, coinsList]);

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateCoboChainField = (tokenKey, field, value) => {
    setCoboChainData((d) => ({
      ...d,
      [tokenKey]: { ...(d[tokenKey] || {}), [field]: value },
    }));
  };

  const updateChainField = (chainId, field, value) => {
    setChainData((d) => ({
      ...d,
      [chainId]: { ...(d[chainId] || {}), [field]: value },
    }));
    if (field === "contract_address") {
      setFieldErrors((e) => {
        const key = `contract_${chainId}`;
        const next = { ...e };
        delete next[key];
        return next;
      });
    }
  };

  const addChain = (chainId) => {
    if (!chainId || chains.includes(chainId)) return;
    setChainData((d) => ({
      ...d,
      [chainId]: {
        contract_address: "",
        decimals: "",
        min_withdrawal: "",
        max_withdrawal: "",
        min_deposit: "",
        max_deposit: "",
        withdrawal_fee: "",
        deposit_status: "ACTIVE",
        withdrawal_status: "ACTIVE",
      },
    }));
  };

  const removeChain = (chainId) => {
    if (chains.length <= 1) return;
    setChainData((d) => {
      const next = { ...d };
      delete next[chainId];
      return next;
    });
    setFieldErrors((e) => {
      const next = { ...e };
      delete next[`contract_${chainId}`];
      return next;
    });
  };

  const addLink = () => setLinks((l) => [...l, ""]);
  const removeLink = (i) => setLinks((l) => l.filter((_, idx) => idx !== i));
  const setLink = (i, v) => setLinks((l) => l.map((x, idx) => (idx === i ? v : x)));

  const availableChainsToAdd = supportedChains.filter((c) => !chains.includes(c.id));

  const buildFormData = (coboEntry = null) => {
    const formData = new FormData();
    if (coinImage) formData.append("coin-image", coinImage);

    formData.append("name", name.trim());
    formData.append("short_name", shortName.trim().toUpperCase());
    formData.append("chain", JSON.stringify(chains));
    formData.append("category", category);
    formData.append("status", status);

    const contract_address = {};
    const decimals = {};
    const min_withdrawal = {};
    const max_withdrawal = {};
    const min_deposit = {};
    const max_deposit = {};
    const withdrawal_fee = {};
    const deposit_status = {};
    const withdrawal_status = {};

    chains.forEach((ch) => {
      const cd = chainData[ch] || {};
      contract_address[ch] = (cd.contract_address || "").trim();
      decimals[ch] = Number(cd.decimals) || 0;
      min_withdrawal[ch] = Number(cd.min_withdrawal) || 0;
      max_withdrawal[ch] = Number(cd.max_withdrawal) || 0;
      min_deposit[ch] = Number(cd.min_deposit) || 0;
      max_deposit[ch] = Number(cd.max_deposit) || 0;
      withdrawal_fee[ch] = Number(cd.withdrawal_fee) || 0;
      deposit_status[ch] = cd.deposit_status || "ACTIVE";
      withdrawal_status[ch] = cd.withdrawal_status || "ACTIVE";
    });

    formData.append("contract_address", JSON.stringify(contract_address));
    formData.append("decimals", JSON.stringify(decimals));
    formData.append("min_withdrawal", JSON.stringify(min_withdrawal));
    formData.append("max_withdrawal", JSON.stringify(max_withdrawal));
    formData.append("min_deposit", JSON.stringify(min_deposit));
    formData.append("max_deposit", JSON.stringify(max_deposit));
    formData.append("withdrawal_fee", JSON.stringify(withdrawal_fee));
    formData.append("deposit_status", JSON.stringify(deposit_status));
    formData.append("withdrawal_status", JSON.stringify(withdrawal_status));

    if (description?.trim()) formData.append("description", description.trim());
    if (totalSupply !== undefined) formData.append("total_supply", String(totalSupply));
    if (circulatingSupply !== undefined) formData.append("circulating_supply", String(circulatingSupply));
    formData.append("swapping_status", swappingStatus);
    formData.append("p2p_status", p2pStatus);
    if (minSwapping !== "" && minSwapping != null) formData.append("min_swapping", String(minSwapping));
    if (maxSwapping !== "" && maxSwapping != null) formData.append("max_swapping", String(maxSwapping));
    if (swappingFee !== "" && swappingFee != null) formData.append("swapping_fee", String(swappingFee));
    if (p2pFee !== "" && p2pFee != null) formData.append("p2p_fee", String(p2pFee));

    const linksToSend = links.filter(Boolean).map((l) => (typeof l === "object" && l?.url ? l : { url: String(l), label: "" }));
    formData.append("links", JSON.stringify(linksToSend));

    appendCoboChainFields(formData, coboEntry);

    return formData;
  };

  const validateForm = () => {
    const errors = {};
    chains.forEach((ch) => {
      const cd = chainData[ch] || {};
      const addr = (cd.contract_address || "").trim();
      if (addr) {
        const err = validateContractAddress(addr, ch, supportedChains);
        if (err) errors[`contract_${ch}`] = err;
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateCurrency = async (e) => {
    e.preventDefault();
    if (!selectedCoinId) return;
    setSubmitError("");
    setSubmitSuccess("");
    setFieldErrors({});
    if (!validateForm()) {
      setSubmitError("Please fix the validation errors below.");
      setOpenSections((prev) => new Set([...prev, "chain"]));
      errorAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setShowConfirmModal(true);
  };

  const performUpdate = async () => {
    if (!selectedCoinId) return;
    setShowConfirmModal(false);
    setSubmitLoading(true);
    setSubmitError("");

    const coboUpdates = getCoboChainUpdates(coinDetail, coboChainData, selectedCoboEntry);
    const formPayloads = coboUpdates.length > 0
      ? coboUpdates.map((entry) => buildFormData(entry))
      : [buildFormData()];

    let lastRes = null;
    for (const formData of formPayloads) {
      lastRes = await AdminService.updateCoin(selectedCoinId, formData);
      if (!lastRes?.success) break;
    }

    setSubmitLoading(false);
    if (lastRes?.success) {
      setSubmitSuccess(
        formPayloads.length > 1
          ? `Currency updated successfully (${formPayloads.length} Cobo chains).`
          : "Currency updated successfully."
      );
      setCoinImage(null);
      setFieldErrors({});
      fetchCoinsList();
    } else {
      const errMsg = lastRes?.message || "Failed to update currency.";
      setSubmitError(errMsg);
      errorAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  if (listLoading) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <section className="dashboard-hero">
          <div className="dashboard-hero__text">
            <p className="dashboard-hero__breadcrumb">
              <Link to="/dashboard/homepage">Dashboard</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <span>Currency Management</span>
            </p>
            <h1 className="dashboard-hero__title">Currency Management</h1>
          </div>
        </section>
        <div className="currency-loading">
          <i className="fas fa-spinner fa-spin" /> Loading coins…
        </div>
      </main>
    );
  }

  if (listError) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <section className="dashboard-hero">
          <div className="dashboard-hero__text">
            <p className="dashboard-hero__breadcrumb">
              <Link to="/dashboard/homepage">Dashboard</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <span>Currency Management</span>
            </p>
            <h1 className="dashboard-hero__title">Currency Management</h1>
          </div>
        </section>
        <div className="currency-error">
          <i className="fas fa-exclamation-triangle" />
          <p>{listError}</p>
          <button type="button" className="btn btn-primary" onClick={fetchCoinsList}>
            <i className="fas fa-redo" /> Retry
          </button>
        </div>
      </main>
    );
  }

  if (coinsList.length === 0) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <section className="dashboard-hero">
          <div className="dashboard-hero__text">
            <p className="dashboard-hero__breadcrumb">
              <Link to="/dashboard/homepage">Dashboard</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <span>Currency Management</span>
            </p>
            <h1 className="dashboard-hero__title">Currency Management</h1>
          </div>
        </section>
        <div className="currency-empty">
          <p>No coins found. Add a coin first from Coin List Requests.</p>
          <Link to="/dashboard/addSpotTradingCoin" className="btn btn-primary">
            <i className="fas fa-plus" /> Add Spot Trading Coin
          </Link>
        </div>
      </main>
    );
  }

  const selectedCoin = coinsList.find((c) => c._id === selectedCoinId);

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero currency-management-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Currency Management</span>
          </p>
          <h1 className="dashboard-hero__title">Currency Management</h1>
          <p className="dashboard-hero__subtitle">Manage and configure cryptocurrency settings</p>
          {selectedCoin && (
            <div className="currency-management-hero__coin">
              <span className="currency-management-hero__coin-label">{selectedCoin.name} ({selectedCoin.short_name})</span>
              <i className="fas fa-coins" />
            </div>
          )}
        </div>
        <div className="currency-management-hero__select">
          <label className="currency-select-label">Select Currency</label>
          <select className="currency-select" value={selectedCoinId} onChange={(e) => setSelectedCoinId(e.target.value)}>
            {coinsList.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name} ({c.short_name})
              </option>
            ))}
          </select>
        </div>
      </section>

      <div className="dashboard-body currency-management-body">
        <form onSubmit={handleUpdateCurrency} className="currency-form">
            {submitError && (
              <div ref={errorAlertRef} className="currency-alert currency-alert--error" role="alert">
                <i className="fas fa-exclamation-circle" />
                <span>{submitError}</span>
              </div>
            )}
            {submitSuccess && (
              <div className="currency-alert currency-alert--success" role="status">
                <i className="fas fa-check-circle" />
                <span>{submitSuccess}</span>
              </div>
            )}

            <Section id="basic" title="Basic Coin Information" icon="fa-info-circle" openSections={openSections} onToggle={toggleSection}>
              <div className="currency-form-row">
                <div className="currency-field">
                  <label>Coin Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Bitcoin" required />
                </div>
                <div className="currency-field">
                  <label>Short Name (Symbol)</label>
                  <input type="text" value={shortName} onChange={(e) => setShortName(e.target.value.toUpperCase())} placeholder="e.g. BTC" style={{ textTransform: "uppercase" }} required />
                </div>
                <div className="currency-field">
                  <label>Category</label>
                  <select value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="GLOBAL">GLOBAL</option>
                    <option value="LOCAL">LOCAL</option>
                  </select>
                </div>
                <div className="currency-field">
                  <label>Status</label>
                  <select value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUS_VALUES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="currency-field">
                <label>Coin Image (optional – replace existing)</label>
                <input type="file" accept={IMAGE_ACCEPT} onChange={(e) => setCoinImage(e.target.files?.[0] || null)} />
                {coinImage && <span className="currency-file-preview"><i className="fas fa-check" /> {coinImage.name}</span>}
              </div>
            </Section>

            <Section id="chain" title="Chain & Contract Information" icon="fa-link" openSections={openSections} onToggle={toggleSection}>
              <div className="currency-chain-actions">
                {availableChainsToAdd.length > 0 && (
                  <div className="currency-add-chain">
                    <select
                      className="currency-add-chain-select"
                      value=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) { addChain(v); e.target.value = ""; }
                      }}
                      aria-label="Add chain"
                    >
                      <option value="">Add chain…</option>
                      {availableChainsToAdd.map((c) => (
                        <option key={c.id} value={c.id}>{c.name || c.id}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {chains.length === 0 ? (
                <p className="currency-muted">No chains configured. Add a chain from the dropdown above (requires config).</p>
              ) : (
                <div className="currency-chain-table-wrap">
                  <table className="currency-chain-table">
                    <thead>
                      <tr>
                        <th>Chain</th>
                        <th>Contract Address</th>
                        <th>Decimals</th>
                        <th>Deposit Status</th>
                        <th>Withdrawal Status</th>
                        <th className="currency-chain-actions-col"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {chains.map((ch) => {
                        const cd = chainData[ch] || {};
                        return (
                          <tr key={ch}>
                            <td><span className="currency-tag">{ch}</span></td>
                            <td className="currency-contract-cell">
                              <input
                                type="text"
                                value={cd.contract_address || ""}
                                onChange={(e) => updateChainField(ch, "contract_address", e.target.value)}
                                placeholder={ch === "TRC20" ? "T..." : ch === "SOLANA" ? "Base58..." : "0x..."}
                                spellCheck={false}
                                className={fieldErrors[`contract_${ch}`] ? "currency-input--error" : ""}
                                aria-invalid={!!fieldErrors[`contract_${ch}`]}
                                aria-describedby={fieldErrors[`contract_${ch}`] ? `contract-err-${ch}` : undefined}
                              />
                              {fieldErrors[`contract_${ch}`] && (
                                <span id={`contract-err-${ch}`} className="currency-field-error">{fieldErrors[`contract_${ch}`]}</span>
                              )}
                            </td>
                            <td>
                              <input type="number" min={0} max={18} value={cd.decimals} onChange={(e) => updateChainField(ch, "decimals", e.target.value)} onWheel={(e) => e.target.blur()} />
                            </td>
                            <td>
                              <select value={cd.deposit_status || "ACTIVE"} onChange={(e) => updateChainField(ch, "deposit_status", e.target.value)}>
                                {ACTIVE_INACTIVE.map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select value={cd.withdrawal_status || "ACTIVE"} onChange={(e) => updateChainField(ch, "withdrawal_status", e.target.value)}>
                                {ACTIVE_INACTIVE.map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </td>
                            <td className="currency-chain-actions-col">
                              <button
                                type="button"
                                className="currency-remove-chain"
                                onClick={() => removeChain(ch)}
                                disabled={chains.length <= 1}
                                title={chains.length <= 1 ? "At least one chain required" : `Remove ${ch}`}
                                aria-label={`Remove ${ch}`}
                              >
                                <i className="fas fa-times" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            <Section id="cobo" title="Cobo Chain Withdrawal Settings" icon="fa-network-wired" openSections={openSections} onToggle={toggleSection}>
              {coboChainKeys.length === 0 ? (
                <p className="currency-muted">No Cobo chains configured for this coin.</p>
              ) : (
                <>
                  <div className="currency-form-row currency-cobo-select-row">
                    <div className="currency-field currency-field--grow">
                      <label>Select Cobo Chain</label>
                      <select
                        value={selectedCoboChainKey}
                        onChange={(e) => setSelectedCoboChainKey(e.target.value)}
                      >
                        {coboChainKeys.map((key) => {
                          const entry = coboChainData[key] || {};
                          return (
                            <option key={key} value={key}>
                              {entry.tokenId || key} — {entry.chainId || entry.chainName || "Unknown chain"}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  {selectedCoboEntry && (
                    <div className="currency-chain-block currency-cobo-editor">
                      <h4 className="currency-chain-block__title">
                        {selectedCoboEntry.tokenId} <span className="currency-cobo-meta">({selectedCoboEntry.chainId})</span>
                      </h4>
                      <div className="currency-form-row currency-form-row--3">
                        <div className="currency-field">
                          <label>Chain ID</label>
                          <input type="text" value={selectedCoboEntry.chainId || ""} readOnly disabled />
                        </div>
                        <div className="currency-field">
                          <label>Token ID</label>
                          <input type="text" value={selectedCoboEntry.tokenId || ""} readOnly disabled />
                        </div>
                        <div className="currency-field">
                          <label>Decimals</label>
                          <input type="text" value={selectedCoboEntry.decimals ?? ""} readOnly disabled />
                        </div>
                      </div>
                      <div className="currency-form-row currency-form-row--3">
                        <div className="currency-field">
                          <label>Min Withdrawal Limit</label>
                          <input
                            type="number"
                            min={0}
                            step="any"
                            value={selectedCoboEntry.minWithdrawalLimit ?? ""}
                            onChange={(e) => updateCoboChainField(selectedCoboChainKey, "minWithdrawalLimit", e.target.value)}
                            onWheel={(e) => e.target.blur()}
                          />
                        </div>
                        <div className="currency-field">
                          <label>Max Withdrawal Limit</label>
                          <input
                            type="number"
                            min={0}
                            step="any"
                            value={selectedCoboEntry.maxWithdrawalLimit ?? ""}
                            onChange={(e) => updateCoboChainField(selectedCoboChainKey, "maxWithdrawalLimit", e.target.value)}
                            onWheel={(e) => e.target.blur()}
                          />
                        </div>
                        <div className="currency-field">
                          <label>Withdrawal Fee</label>
                          <input
                            type="number"
                            min={0}
                            step="any"
                            value={selectedCoboEntry.withdrawalFee ?? ""}
                            onChange={(e) => updateCoboChainField(selectedCoboChainKey, "withdrawalFee", e.target.value)}
                            onWheel={(e) => e.target.blur()}
                          />
                        </div>
                      </div>
                      <div className="currency-form-row">
                        <div className="currency-field currency-field--grow">
                          <label>Chain status</label>
                          <select
                            value={selectedCoboEntry.isActive ? "true" : "false"}
                            onChange={(e) =>
                              updateCoboChainField(
                                selectedCoboChainKey,
                                "isActive",
                                e.target.value === "true"
                              )
                            }
                          >
                            <option value="true">Active — withdrawals enabled on this chain</option>
                            <option value="false">Inactive — chain disabled for withdrawals</option>
                          </select>
                        </div>
                      </div>
                      <p className="currency-muted currency-cobo-hint">
                        Confirmations: {selectedCoboEntry.confirmations ?? "—"}
                        {selectedCoboEntry.requireMemo ? " · Memo required" : ""}
                      </p>
                    </div>
                  )}

                  <div className="currency-chain-table-wrap currency-cobo-table-wrap">
                    <table className="currency-chain-table currency-cobo-table">
                      <thead>
                        <tr>
                          <th>Token ID</th>
                          <th>Chain ID</th>
                          <th>Status</th>
                          <th>Min Withdrawal Limit</th>
                          <th>Max Withdrawal Limit</th>
                          <th>Withdrawal Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coboChainKeys.map((key) => {
                          const entry = coboChainData[key] || {};
                          const isSelected = key === selectedCoboChainKey;
                          return (
                            <tr
                              key={key}
                              className={isSelected ? "currency-cobo-row--selected" : ""}
                              onClick={() => setSelectedCoboChainKey(key)}
                            >
                              <td><span className="currency-tag">{entry.tokenId || key}</span></td>
                              <td>{entry.chainId || "—"}</td>
                              <td>
                                <span
                                  className={`currency-cobo-status ${
                                    entry.isActive ? "currency-cobo-status--active" : "currency-cobo-status--inactive"
                                  }`}
                                >
                                  {entry.isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td>{entry.minWithdrawalLimit ?? "—"}</td>
                              <td>{entry.maxWithdrawalLimit ?? "—"}</td>
                              <td>{entry.withdrawalFee ?? "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </Section>

            <Section id="withdrawal" title="Legacy Withdrawal Settings (BEP20 / ERC20 / TRC20)" icon="fa-arrow-up" openSections={openSections} onToggle={toggleSection}>
              <p className="currency-muted" style={{ marginTop: 0 }}>
                Legacy chain keys used for internal wallet configuration. Cobo withdrawal limits are managed in the section above.
              </p>
              <div className="currency-chain-grid">
                {chains.map((ch) => {
                  const cd = chainData[ch] || {};
                  return (
                    <div key={ch} className="currency-chain-block">
                      <h4 className="currency-chain-block__title">{ch}</h4>
                      <div className="currency-form-row currency-form-row--3">
                        <div className="currency-field">
                          <label>Min Withdrawal</label>
                          <input type="number" min={0} step="any" value={cd.min_withdrawal} onChange={(e) => updateChainField(ch, "min_withdrawal", e.target.value)} onWheel={(e) => e.target.blur()} />
                        </div>
                        <div className="currency-field">
                          <label>Max Withdrawal</label>
                          <input type="number" min={0} step="any" value={cd.max_withdrawal} onChange={(e) => updateChainField(ch, "max_withdrawal", e.target.value)} onWheel={(e) => e.target.blur()} />
                        </div>
                        <div className="currency-field">
                          <label>Withdrawal Fee</label>
                          <input type="number" min={0} step="any" value={cd.withdrawal_fee} onChange={(e) => updateChainField(ch, "withdrawal_fee", e.target.value)} onWheel={(e) => e.target.blur()} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section id="deposit" title="Deposit Settings" icon="fa-arrow-down" openSections={openSections} onToggle={toggleSection}>
              <div className="currency-chain-grid">
                {chains.map((ch) => {
                  const cd = chainData[ch] || {};
                  return (
                    <div key={ch} className="currency-chain-block">
                      <h4 className="currency-chain-block__title">{ch}</h4>
                      <div className="currency-form-row currency-form-row--3">
                        <div className="currency-field">
                          <label>Min Deposit</label>
                          <input type="number" min={0} step="any" value={cd.min_deposit} onChange={(e) => updateChainField(ch, "min_deposit", e.target.value)} onWheel={(e) => e.target.blur()} />
                        </div>
                        <div className="currency-field">
                          <label>Max Deposit</label>
                          <input type="number" min={0} step="any" value={cd.max_deposit} onChange={(e) => updateChainField(ch, "max_deposit", e.target.value)} onWheel={(e) => e.target.blur()} />
                        </div>
                        <div className="currency-field" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section id="status" title="Status & Supply" icon="fa-toggle-on" openSections={openSections} onToggle={toggleSection}>
              <div className="currency-form-row">
                <div className="currency-field">
                  <label>Swapping Status</label>
                  <select value={swappingStatus} onChange={(e) => setSwappingStatus(e.target.value)}>
                    {ACTIVE_INACTIVE.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="currency-field">
                  <label>P2P Status</label>
                  <select value={p2pStatus} onChange={(e) => setP2pStatus(e.target.value)}>
                    {ACTIVE_INACTIVE.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="currency-field">
                  <label>Total Supply</label>
                  <input type="text" value={totalSupply} onChange={(e) => setTotalSupply(e.target.value)} placeholder="Optional" />
                </div>
                <div className="currency-field">
                  <label>Circulating Supply</label>
                  <input type="text" value={circulatingSupply} onChange={(e) => setCirculatingSupply(e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div className="currency-form-row">
                <div className="currency-field">
                  <label>Min Swapping</label>
                  <input type="number" min={0} step="any" value={minSwapping} onChange={(e) => setMinSwapping(e.target.value)} onWheel={(e) => e.target.blur()} placeholder="Optional" />
                </div>
                <div className="currency-field">
                  <label>Max Swapping</label>
                  <input type="number" min={0} step="any" value={maxSwapping} onChange={(e) => setMaxSwapping(e.target.value)} onWheel={(e) => e.target.blur()} placeholder="Optional" />
                </div>
                <div className="currency-field">
                  <label>Swapping Fee</label>
                  <input type="number" min={0} step="any" value={swappingFee} onChange={(e) => setSwappingFee(e.target.value)} onWheel={(e) => e.target.blur()} placeholder="Optional" />
                </div>
                <div className="currency-field">
                  <label>P2P Fee</label>
                  <input type="number" min={0} step="any" value={p2pFee} onChange={(e) => setP2pFee(e.target.value)} onWheel={(e) => e.target.blur()} placeholder="Optional" />
                </div>
              </div>
            </Section>

            <Section id="info" title="Additional Information" icon="fa-align-left" openSections={openSections} onToggle={toggleSection}>
              <div className="currency-field">
                <label>Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Coin description..." />
              </div>
            </Section>

            <Section id="links" title="Custom Links" icon="fa-external-link-alt" openSections={openSections} onToggle={toggleSection}>
              {links.map((link, i) => (
                <div key={i} className="currency-link-row">
                  <input type="url" value={typeof link === "object" && link ? (link.url || "") : (link || "")} onChange={(e) => setLink(i, e.target.value)} placeholder="https://..." className="currency-link-input" />
                  <button type="button" className="btn-remove-link" onClick={() => removeLink(i)}>Remove</button>
                </div>
              ))}
              <button type="button" className="btn-add-link" onClick={addLink}>
                <i className="fas fa-plus" /> Add Link
              </button>
            </Section>

            <div className="currency-form-footer">
              <button type="submit" className="btn-update-currency" disabled={submitLoading || !coinDetail}>
                {submitLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-sync-alt" />}
                {submitLoading ? " Updating…" : " Update Currency"}
              </button>
            </div>
        </form>

        {showConfirmModal && (() => {
          const changes = getChangesSummary(coinDetail, {
            name, shortName, category, status, description, totalSupply, circulatingSupply,
            swappingStatus, p2pStatus, minSwapping, maxSwapping, swappingFee, p2pFee,
            chainData, coboChainData, links, coinImage,
          });
          return (
            <div className="currency-confirm-overlay" onClick={() => setShowConfirmModal(false)}>
              <div className="currency-confirm-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="currency-confirm-title">Confirm Update</h3>
                <p className="currency-confirm-text">
                  You are about to update {selectedCoin && `${selectedCoin.name} (${selectedCoin.short_name})`}. Review the changes below:
                </p>
                {changes.length > 0 ? (
                  <div className="currency-changes-list">
                    {changes.map((c, i) => (
                      <div key={i} className="currency-change-row">
                        <span className="currency-change-field">{c.field}</span>
                        <span className="currency-change-from">{c.from}</span>
                        <span className="currency-change-arrow"><i className="fas fa-arrow-right" /></span>
                        <span className="currency-change-to">{c.to}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="currency-confirm-note">No changes detected (e.g. only coin image replaced).</p>
                )}
                <div className="currency-confirm-actions">
                  <button type="button" className="btn-currency-cancel" onClick={() => setShowConfirmModal(false)}>{changes.length > 0 ? "Cancel" : "Close"}</button>
                  {changes.length > 0 && (
                    <button type="button" className="btn-currency-confirm" onClick={performUpdate} disabled={submitLoading}>
                      {submitLoading ? <><i className="fas fa-spinner fa-spin" /> Updating…</> : "Update"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </main>
  );
};

export default CurrencyManagementPage;
